<template>
  <component
    :is="tag"
    class="badge"
    :aria-label="label"
    v-bind="$attrs"
  >
    <span
      class="badge__content"
      :class="{
        [`badge__content--size--${size}`]: true,
        [`badge__content--${label}`]: true,
        'badge__content--high-contrast': highContrast,
      }"
      :style="{
        backgroundColor: parsedColor.bg,
        color: parsedColor.text,
      }"
    >
      <slot name="prefix-icon" />
      <ToolTip
        :content="tooltip"
        placement="bottom"
        :delay="[300, 300]"
      >
        <span
          class="badge__content__label"
          :class="{
            [`badge__content__label--${iconPosition}`]: true,
            'badge__content__label--capitalize': !noCapitalize,
          }"
        >
          <slot>
            {{ label }}
          </slot>
        </span>
      </ToolTip>
      <slot name="suffix-icon">
        <ToolTip
          :content="tooltipDelete"
          placement="bottom"
          :delay="[300, 300]"
        >
          <Icon
            v-if="deletable"
            class="badge__content__delete"
            name="mono-close"
            aria-label="Delete"
            @click="$emit('delete', label)"
          />
        </ToolTip>
      </slot>
    </span>
  </component>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { computed, useSlots } from 'vue'

import type { RGBA } from '@/uiKit/colorPalette'
import { getDesaturatedRainbowColor, getTextRainbowColor } from '@/uiKit/colorPalette'
import { anyToRGBA } from '@/uiKit/colorPalette'
import { rgbaString } from '@/uiKit/colorPalette'
import { getPaletteColor } from '@/uiKit/colorPalette'
import ToolTip from '@/uiKit/ToolTip/ToolTip.vue'
import Icon from '@/uiKit/Icon/Icon.vue'

type ButtonIconPosition = 'left' | 'right' | 'both' | 'none'

const getIconPosition = (hasPrefix: boolean, hasSuffix: boolean): ButtonIconPosition => {
  const hasBoth = hasSuffix && hasPrefix
  const hasNone = !hasSuffix && !hasPrefix

  if (hasBoth) {
    return 'both'
  }
  if (hasNone) {
    return 'none'
  }
  if (hasSuffix) {
    return 'right'
  }

  return 'left'
}

const props = // eslint-disable-next-line vue/define-props-declaration
  defineProps({
    label: { type: String, default: '' },
    overrideTooltip: { type: String, default: '' },
    size: {
      type: String,
      default: 'small',
      validator: (val: string): boolean => ['small', 'medium', 'large'].includes(val),
    },
    tag: {
      type: String,
      default: 'div',
      validator: (val: string): boolean => ['a', 'div', 'router-link', 'button'].includes(val),
    },
    color: { type: [Object, String] as PropType<RGBA | string> | undefined, default: undefined },
    alpha: { type: Number, default: 0.1 },
    highContrast: { type: Boolean, default: false },
    overrideLabelColor: { default: '', type: String },
    deletable: { type: Boolean, default: false },
    noTooltip: { type: Boolean, default: false },
    noCapitalize: { type: Boolean, default: false },
  })

defineEmits<{
  (e: 'delete', value: string): void
}>()

const slots = useSlots()

const hasPrefix = computed((): boolean => !!slots['prefix-icon'])

const hasSuffix = computed((): boolean => props.deletable || !!slots['suffix-icon'])

/**
 * Sets icon position automatically instead of passing it as prop. We need that to adjust
 * padding when passing icons/slots
 */
const iconPosition = computed(
  (): ButtonIconPosition => getIconPosition(hasPrefix.value, hasSuffix.value),
)

const parsedColor = computed(() => {
  const alphaValue = props.highContrast ? 1 : props.alpha
  if (props.color) {
    if (typeof props.color === 'string') {
      return {
        bg: props.color,
        text: props.overrideLabelColor ? props.overrideLabelColor : 'rgba(255, 255, 255, 1)',
      }
    }

    const desaturatedColor = getDesaturatedRainbowColor(props.color)
    const textColor = getTextRainbowColor(props.color)

    return {
      bg: desaturatedColor || rgbaString(props.color, alphaValue),
      text: props.overrideLabelColor
        ? props.overrideLabelColor
        : props.highContrast
          ? 'rgba(255, 255, 255, 1)'
          : textColor || rgbaString(props.color),
    }
  }
  const normal = anyToRGBA(getPaletteColor(props.label, 'normal'))
  const saturated = anyToRGBA(getPaletteColor(props.label, 'saturated'))
  const background = anyToRGBA(getPaletteColor(props.label, 'desaturated'))
  return {
    bg: props.highContrast ? rgbaString(saturated) : rgbaString(background),
    text: props.overrideLabelColor
      ? props.overrideLabelColor
      : props.highContrast
        ? 'rgba(255, 255, 255, 1)'
        : rgbaString(normal),
  }
})

const tooltip = computed(() => {
  if (props.noTooltip) {
    return undefined
  }
  return props.overrideTooltip || props.label
})

const tooltipDelete = computed(() => {
  if (props.noTooltip) {
    return undefined
  }
  return `Clear ${props.overrideTooltip || props.label}`
})
</script>

<style lang="scss" scoped>
.badge {
  @include row--center;

  .badge__content {
    max-width: 100%;
    display: flex;
    align-items: center;
    flex-direction: row;
  }

  &__content {
    @include row--center;
    align-items: center;
    background-color: $colorStatusWarningSurface;
    transition: background-color 150ms ease;

    &--high-contrast {
      .badge__content__delete {
        color: white !important;
      }
    }

    &--size {
      &--small {
        height: 16px;
        min-width: 16px;
        padding: 0 4px;
        border-radius: 4px;

        .badge__content {
          &__label {
            @include typographyRegularBody25;
            line-height: 16px;
          }

          &__delete {
            height: 12px;
            min-width: 12px;
            width: 12px;
          }
        }
      }

      &--medium {
        height: 20px;
        min-width: 16px;
        padding: 0 4px;
        border-radius: 4px;

        .badge__content {
          &__label {
            @include typographyRegularBody50;
            line-height: 20px;
          }

          &__delete {
            height: 14px;
            min-width: 14px;
            width: 14px;
          }
        }
      }

      &--large {
        height: 24px;
        min-width: 24px;
        padding: 0 6px;
        border-radius: 6px;

        .badge__content {
          &__label {
            @include typographyRegularBody100;
            line-height: 24px;
          }

          &__delete {
            height: 16px;
            min-width: 16px;
            width: 16px;
          }
        }
      }
    }

    &__label {
      display: block;
      justify-content: flex-start;
      align-items: center;
      height: 100%;
      text-align: center;
      white-space: nowrap;
      @include ellipsis;

      &--capitalize {
        text-transform: capitalize;
      }

      &--left {
        margin: 0 0 0 4px;
      }

      &--right {
        margin: 0 4px 0 0;
      }

      &--both {
        margin: 0 4px;
      }

      &--none {
        margin: 0;
      }
    }

    &__delete {
      opacity: 0.5;
      cursor: pointer;

      &:hover {
        opacity: 1;
      }

      &:focus,
      &:active {
        outline: none;
      }
    }
  }
}

:deep(.tooltip__target) {
  max-width: 100%;
}
</style>
